/**
 * Trunnion AI - Node Type Registry
 * Maps node types to components and provides default data factories.
 */

import type { ComponentType } from 'react';
import type { NodeProps } from '@xyflow/react';
import type {
  WorkflowNodeType,
  WorkflowNodeData,
  AgentNodeData,
  HumanGateNodeData,
  ConditionalNodeData,
  ParallelNodeData,
  TriggerNodeData,
  TerminalNodeData,
  SubworkflowNodeData,
} from '@/types';
import { AgentNode } from './AgentNode';
import { HumanGateNode } from './HumanGateNode';
import { ConditionalNode } from './ConditionalNode';
import { ParallelForkNode } from './ParallelForkNode';
import { ParallelJoinNode } from './ParallelJoinNode';
import { TriggerNode } from './TriggerNode';
import { TerminalNode } from './TerminalNode';

export const nodeTypes: Record<string, ComponentType<NodeProps>> = {
  agent: AgentNode as unknown as ComponentType<NodeProps>,
  humanGate: HumanGateNode as unknown as ComponentType<NodeProps>,
  conditional: ConditionalNode as unknown as ComponentType<NodeProps>,
  parallelFork: ParallelForkNode as unknown as ComponentType<NodeProps>,
  parallelJoin: ParallelJoinNode as unknown as ComponentType<NodeProps>,
  trigger: TriggerNode as unknown as ComponentType<NodeProps>,
  terminal: TerminalNode as unknown as ComponentType<NodeProps>,
};

let nodeIdCounter = 0;

export function generateNodeId(type: string): string {
  return `${type}-${Date.now()}-${++nodeIdCounter}`;
}

export const defaultNodeData: Record<
  WorkflowNodeType,
  () => WorkflowNodeData
> = {
  agent: (): AgentNodeData => ({
    nodeType: 'agent',
    label: 'New Agent',
    agentId: '',
    agentName: '',
    tier: 'specialist',
    mode: 'supervised',
    classification: 'UNCLASSIFIED',
    toolCount: 0,
    inputMapping: {},
    outputMapping: {},
    timeoutSeconds: 300,
    retryPolicy: {
      maxRetries: 1,
      backoffMultiplier: 2,
    },
  }),

  humanGate: (): HumanGateNodeData => ({
    nodeType: 'humanGate',
    label: 'Approval Gate',
    description: 'Requires human approval to proceed',
    approverRoles: ['operator', 'admin'],
    timeoutMinutes: 60,
    classification: 'UNCLASSIFIED',
    requiresJustification: true,
  }),

  conditional: (): ConditionalNodeData => ({
    nodeType: 'conditional',
    label: 'Condition',
    condition: '',
    conditionType: 'expression',
    branches: [
      { label: 'True', targetHandleId: 'true', condition: 'true' },
      { label: 'False', targetHandleId: 'false', condition: 'false' },
    ],
  }),

  parallelFork: (): ParallelNodeData => ({
    nodeType: 'parallelFork',
    label: 'Parallel Fork',
    strategy: 'all',
    timeoutSeconds: 600,
  }),

  parallelJoin: (): ParallelNodeData => ({
    nodeType: 'parallelJoin',
    label: 'Parallel Join',
    strategy: 'all',
    timeoutSeconds: 600,
  }),

  trigger: (): TriggerNodeData => ({
    nodeType: 'trigger',
    label: 'Trigger',
    triggerType: 'manual',
    inputSchema: {},
  }),

  terminal: (): TerminalNodeData => ({
    nodeType: 'terminal',
    label: 'End',
    terminalType: 'success',
    outputMapping: {},
    notifyOnComplete: true,
  }),

  subworkflow: (): SubworkflowNodeData => ({
    nodeType: 'subworkflow',
    label: 'Sub-workflow',
    workflowId: '',
    workflowName: '',
    inputMapping: {},
    outputMapping: {},
    classification: 'UNCLASSIFIED',
  }),
};

export interface NodePaletteItem {
  type: WorkflowNodeType;
  label: string;
  description: string;
  icon: string;
  category: 'execution' | 'control' | 'flow';
}

export const nodePaletteItems: NodePaletteItem[] = [
  {
    type: 'trigger',
    label: 'Trigger',
    description: 'Workflow entry point',
    icon: 'Zap',
    category: 'flow',
  },
  {
    type: 'agent',
    label: 'Agent',
    description: 'AI agent execution node',
    icon: 'Bot',
    category: 'execution',
  },
  {
    type: 'humanGate',
    label: 'Human Gate',
    description: 'Human approval checkpoint',
    icon: 'UserCheck',
    category: 'control',
  },
  {
    type: 'conditional',
    label: 'Conditional',
    description: 'Branch based on conditions',
    icon: 'GitBranch',
    category: 'control',
  },
  {
    type: 'parallelFork',
    label: 'Parallel Fork',
    description: 'Split into parallel paths',
    icon: 'GitFork',
    category: 'flow',
  },
  {
    type: 'parallelJoin',
    label: 'Parallel Join',
    description: 'Merge parallel paths',
    icon: 'GitMerge',
    category: 'flow',
  },
  {
    type: 'terminal',
    label: 'Terminal',
    description: 'Workflow exit point',
    icon: 'CircleStop',
    category: 'flow',
  },
  {
    type: 'subworkflow',
    label: 'Sub-workflow',
    description: 'Nested workflow execution',
    icon: 'Workflow',
    category: 'execution',
  },
];
